import { fbt } from 'fbtee'
import { formatGenres } from '@shared/genres'
import { useLocation } from 'react-router-dom'
import { ChevronRight, Heart, MapPin, Music } from 'lucide-react'
import { Button } from './ui/button'
import { SectionHeader } from './SectionHeader'
import { ArtistBio } from './ArtistBio'
import { cn } from '../lib/utils'
import { track } from '../lib/analytics'
import { useTransitionNavigate } from '../lib/viewTransition'
import { useArtistDetails } from '../hooks/useArtistDetails'
import type { Artist } from '../types'

export interface ArtistHeart {
  followed: boolean
  /** Followed app-wide but not hearted for this event: a rose-outlined, unfilled heart. */
  followHint?: boolean
  onToggle: () => void
}

export function SetDetailArtistSection({
  artist,
  showHeading,
  showGenre,
  heart,
}: {
  artist: Artist
  showHeading: boolean
  showGenre: boolean
  /** A lineup act's detail: a heart per performer (and for the collab), beside its heading. */
  heart?: ArtistHeart
}) {
  const { details, loading } = useArtistDetails(artist.id)
  const navigate = useTransitionNavigate()
  // Festival mode keeps people in the now: the artist page would drop them out
  // of it, and where else an artist plays isn't what they need mid-festival.
  const inFestivalMode = useLocation().pathname.startsWith('/live-festival/')

  const genres = showGenre ? formatGenres(artist.genres) : null
  const openArtist = () => {
    track('artist_detail_opened', { artist_id: artist.id, entry_point: 'sets' })
    navigate(`/artists/${artist.id}`)
  }

  return (
    <div className="space-y-2">
      {showHeading && (
        <SectionHeader action={heart && <ArtistHeartButton name={artist.name} heart={heart} />}>
          {inFestivalMode ? artist.name : (
            <button type="button" onClick={openArtist} className="cursor-pointer text-left uppercase hover:text-foreground">
              {artist.name}
            </button>
          )}
        </SectionHeader>
      )}
      {(details?.country || genres) && (
        <div className={cn('flex flex-col items-start gap-1 text-xs text-muted-foreground', showHeading ? '-mt-1' : '')}>
          {genres && (
            <span className="inline-flex max-w-full items-center gap-1">
              <Music className="size-3 shrink-0" aria-hidden />
              <span className="truncate">{genres}</span>
            </span>
          )}
          {details?.country && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="size-3" aria-hidden />
              {details.country}
            </span>
          )}
        </div>
      )}
      <ArtistBio details={details} loading={loading} compact />
      {!inFestivalMode && (
        <Button
          variant="link"
          size="sm"
          className="h-auto px-0 gap-1"
          onClick={openArtist}
        >
          {fbt('View artist profile', 'Link from the set dialog to the artist page (bio and every event they play)')}
          <ChevronRight className="size-3.5" aria-hidden />
        </Button>
      )}
    </div>
  )
}

function ArtistHeartButton({ name, heart }: { name: string; heart: ArtistHeart }) {
  const hinted = heart.followHint && !heart.followed
  return (
    <button
      type="button"
      onClick={heart.onToggle}
      className="shrink-0 cursor-pointer p-1.5 -mr-1.5"
      aria-label={heart.followed
        ? fbt('Remove interest in ' + fbt.param('artist', name), 'Accessible label for the heart that un-hearts one artist of a b2b or collab act for this event, in its detail dialog')
        : fbt('Mark ' + fbt.param('artist', name) + ' interested', 'Accessible label for the heart that hearts one artist of a b2b or collab act for this event, in its detail dialog')}
      aria-pressed={heart.followed}
    >
      <Heart
        data-testid={hinted ? 'followed-artist-hint' : undefined}
        className={cn('size-5', heart.followed ? 'fill-interested text-interested' : hinted ? 'text-interested' : 'text-muted-foreground')}
      />
    </button>
  )
}
